import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { useTranslation } from "react-i18next";
import CustomModal from "../../../components/modal/CustomModal";
import { InputReactHookForm } from "../../../components/input/InputReactHookForm";
import CustomSelect, { MenuData } from "../../../components/input/CustomSelect";
import { common, financedEmissions } from "../../../i18n";
import { BorrowerDetail, ListingStatus } from "../../../models/dto/cat15/cat15.types";
import { getSectors } from "../../../service/cat15.service";
import SectorIsicPicker from "../../../components/cat15/SectorIsicPicker";

interface Props {
  open: boolean;
  onClose: () => void;
  borrower?: BorrowerDetail;
  /** Only the fields that differ from what the modal opened with; not called when nothing changed. */
  onSubmit?: (changed: Partial<EditBorrowerForm>) => void;
}

export interface EditBorrowerForm {
  nameEN: string;
  nameTH: string;
  /** ISIC code — a BOT industry code, or a section that has none. PRIMARY sector. */
  sectorIsic: string;
  /** TSIC section code — optional secondary classification. Blank = keep the current one. */
  sectorTsic: string;
  /** "" = not provided; offered only while the borrower's listing is still empty. */
  listingStatus: ListingStatus | "";
}

const toMenuData = (s: { value: string; labelEn: string; labelTh: string }): MenuData => ({
  value: s.value,
  nameEN: s.labelEn,
  nameTH: s.labelTh,
});

const EditBorrowerModal = ({ open, onClose, borrower, onSubmit }: Props) => {
  const { t } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });

  const [tsicOptions, setTsicOptions] = useState<MenuData[]>([]);
  const [isicError, setIsicError] = useState(false);

  useEffect(() => {
    if (!open) return;
    let active = true;
    getSectors("tsic").then((s) => active && setTsicOptions(s.map(toMenuData)));
    return () => {
      active = false;
    };
  }, [open]);

  const { control, handleSubmit, watch, setValue, reset } =
    useForm<EditBorrowerForm>({
      defaultValues: {
        nameEN: "",
        nameTH: "",
        sectorIsic: "",
        sectorTsic: "",
        listingStatus: "",
      },
    });
  const opened = useRef<EditBorrowerForm>();
  // Clearing a set listing is unsupported, so "not provided" is only offered while it is still empty.
  const listingWasEmpty = !borrower?.listingStatus;

  // Prefill each time the modal opens from the borrower's own EN/TH names
  // (falling back to the primary `name` for EN). The TSIC dropdown starts blank
  // (pick to change, blank = keep current — taxId is not editable).
  useEffect(() => {
    if (open && borrower) {
      opened.current = {
        nameEN: borrower.nameEn ?? borrower.name ?? "",
        nameTH: borrower.nameTh ?? "",
        // opens on the borrower's own BOT-level code; the section rollup in `sectionCode` would downgrade it on save
        sectorIsic: borrower.sectorIsicCode ?? "",
        sectorTsic: "",
        listingStatus: borrower.listingStatus ?? "",
      };
      reset(opened.current);
      setIsicError(false);
    }
  }, [open, borrower, reset]);

  const sectorIsic = watch("sectorIsic");
  const sectorTsic = watch("sectorTsic");
  const listingStatus = watch("listingStatus");

  const submit = (values: EditBorrowerForm) => {
    // ISIC is the primary sector — block save when it's empty.
    if (!values.sectorIsic) {
      setIsicError(true);
      return;
    }
    const changed = (Object.keys(values) as (keyof EditBorrowerForm)[]).filter(
      (field) => values[field] !== opened.current?.[field],
    );
    if (changed.length) onSubmit?.(Object.fromEntries(changed.map((field) => [field, values[field]])));
    setIsicError(false);
    onClose();
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      maxWidth="560px"
      header={
        <Box display="flex" alignItems="center" gap="8px">
          <EditOutlinedIcon />
          <Typography variant="h1">{t("Edit-Borrower")}</Typography>
        </Box>
      }
      body={
        <Box display="flex" flexDirection="column" gap="20px">
          {borrower && (
            <Typography variant="body2" color="var(--gray9)">
              {t("Tax-ID")}: {borrower.taxId}
            </Typography>
          )}
          <Grid container spacing="20px">
            <Grid xs={12} sm={6}>
              <InputReactHookForm
                required
                name="nameEN"
                control={control}
                label={t("Name-EN")}
                placeholder="Company name"
                rules={{ required: t("Name-EN") }}
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <InputReactHookForm
                name="nameTH"
                control={control}
                label={t("Name-TH")}
                placeholder="ชื่อบริษัท"
              />
            </Grid>
          </Grid>
          {/* PRIMARY sector = ISIC (section → BOT code); TSIC is optional, blank = keep current. */}
          <SectorIsicPicker
            key={`${borrower?.id ?? ""}-${open}`}
            name="sectorIsic"
            control={control}
            value={sectorIsic}
            onChange={(code) => {
              setValue("sectorIsic", code);
              setIsicError(false);
            }}
            error={isicError}
            errorMessage={t("Sector-ISIC-Required")}
            unavailableMessage={t("Sector-ISIC-Unavailable")}
          />
          <CustomSelect
            label={t("Sector-TSIC")}
            placeholder={t("Sector-Select-Optional")}
            value={sectorTsic}
            listMenuItem={tsicOptions}
            handleValeOnChange={(e) => setValue("sectorTsic", e.target.value)}
            resetValue={() => setValue("sectorTsic", "")}
          />
          <Box>
            <Typography variant="body2" color="var(--gray9)" mb="4px">
              {t("Listing-Status")}
            </Typography>
            <RadioGroup
              row
              value={listingStatus}
              onChange={(e) =>
                setValue("listingStatus", e.target.value as ListingStatus | "")
              }
            >
              <FormControlLabel
                value="listed"
                control={<Radio />}
                label={t("Listed")}
              />
              <FormControlLabel
                value="unlisted"
                control={<Radio />}
                label={t("Unlisted")}
              />
              {listingWasEmpty && (
                <FormControlLabel
                  value=""
                  control={<Radio />}
                  label={t("Not-Provided")}
                />
              )}
            </RadioGroup>
            {listingStatus === "" && (
              <Typography variant="body2" color="var(--gray9)">
                {t("Listing-Not-Provided-Helper")}
              </Typography>
            )}
          </Box>
          <Box display="flex" justifyContent="flex-end" gap="12px">
            <Button variant="outlined" color="secondary" onClick={onClose}>
              {t("Cancel")}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSubmit(submit)}
            >
              {t("Save-Changes")}
            </Button>
          </Box>
        </Box>
      }
    />
  );
};

export default EditBorrowerModal;
